// InvoiceNewDialog — создание счёта на сделку (V14 W6, §3.4/§3.8).
// POST /api/invoices {dealId, amount?, currency?, invoiceDate?, dueDate?} → 201 DRAFT
// (amount/currency/invoiceDate default: из сделки / сделки / now()).
// Сделка — DealSelect (автокомплит, канон EntityBindingSelect: первые 100 + клиентская
// фильтрация); при presetDealId (InvoiceBlock «+ Счёт») — заблокирован на эту сделку.
// Валюта — GET /api/currencies (active); хардкод DEAL_CURRENCIES снят (V16 W10).

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, type TextFieldProps } from '@mui/material';
import { dealsApi } from '@/api/deals';
import { CURRENCIES_LOAD_ERROR, useActiveCurrencies } from '@/api/currencies';
import { invoicesApi, type Invoice } from '@/api/invoices';
import { DateTimeField } from '@/components/forms';
import { parseAmount } from '@/lib/money';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Сколько сделок запрашиваем для автокомплита (backend size ≤ 100). */
const OPTIONS_PAGE_SIZE = 100;

/** staleTime для справочника сделок (меняются редко). */
const OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;

/** Вариант сделки в автокомплите (название, а не id — U-15). */
interface DealOption {
  id: string;
  label: string;
}

interface InvoiceNewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Предзаполненная сделка (InvoiceBlock «+ Счёт»); null — свободный выбор. */
  presetDealId?: string | null;
  /** Колбэк успешного создания (например, бейдж/refetch). */
  onCreated?: (invoice: Invoice) => void;
}

/** Диалог создания счёта (DRAFT; выставление — из списка/карточки, §3.4). */
export function InvoiceNewDialog({ open, onClose, presetDealId = null, onCreated }: InvoiceNewDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const catalogQuery = useActiveCurrencies();
  const currencies = catalogQuery.data ?? [];
  const [dealId, setDealId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<string>('RUB');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');

  // Сброс формы под диалог: preset при каждом открытии (канон PaymentNewDialog).
  useEffect(() => {
    if (open) {
      setDealId(presetDealId ?? '');
      setAmount('');
      setCurrency('RUB');
      setInvoiceDate('');
      setDueDate('');
    }
  }, [open, presetDealId]);

  // Варианты сделок + резолв preset (заголовок для locked-режима, канон EntityBindingSelect).
  const dealsQuery = useQuery({
    queryKey: ['deal-options'],
    queryFn: async (): Promise<DealOption[]> => {
      const page = await dealsApi.list({ page: 0, size: OPTIONS_PAGE_SIZE });
      return page.content.map((d) => ({ id: d.id, label: d.title }));
    },
    staleTime: OPTIONS_STALE_TIME_MS,
    retry: false,
  });
  const presetDealQuery = useQuery({
    queryKey: ['deal', presetDealId],
    queryFn: () => dealsApi.get(presetDealId ?? ''),
    enabled: Boolean(presetDealId),
  });

  const options = dealsQuery.data ?? [];
  const selectedDeal =
    presetDealId != null
      ? ({ id: presetDealId, label: presetDealQuery.data?.title ?? '' } as DealOption)
      : (options.find((o) => o.id === dealId) ?? null);

  const parsedAmount = parseAmount(amount);
  const amountInvalid = amount.trim() !== '' && (parsedAmount === null || parsedAmount < 0);
  const canSubmit = Boolean(dealId) && !amountInvalid;

  const createMutation = useMutation({
    mutationFn: () => {
      if (!dealId) throw new Error('Сделка обязательна');
      return invoicesApi.create({
        dealId,
        ...(parsedAmount != null ? { amount: parsedAmount } : {}),
        currency,
        // Дата уже ISO Instant (DateTimeField, V15-a) — как есть, без new Date().
        ...(invoiceDate ? { invoiceDate } : {}),
        ...(dueDate ? { dueDate } : {}),
      });
    },
    onSuccess: (invoice) => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      onCreated?.(invoice);
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('invoices.create.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {createMutation.isError ? (
            <Alert severity="error">{extractApiErrorMessage(createMutation.error, t('invoices.create.error'))}</Alert>
          ) : null}
          <Autocomplete
            value={selectedDeal}
            options={options}
            loading={dealsQuery.isLoading}
            disabled={Boolean(presetDealId)}
            getOptionLabel={(option: DealOption) => option.label}
            isOptionEqualToValue={(option, current) => option.id === current.id}
            onChange={(_event, option) => setDealId(option?.id ?? '')}
            renderInput={(params) => (
              <TextField
                // AutocompleteRenderInputParams несёт явные undefined (size и др.), что под
                // exactOptionalPropertyTypes несовместимо с TextFieldProps — точечный cast
                // (канон DepartmentAutocomplete.tsx:80-84 / EntityBindingSelect.tsx:125-128).
                {...(params as unknown as TextFieldProps)}
                fullWidth
                required
                label={t('invoices.deal')}
                inputProps={{ ...params.inputProps, 'aria-label': t('invoices.deal') }}
              />
            )}
          />
          <TextField
            label={t('invoices.amount')}
            fullWidth
            value={amount}
            error={amountInvalid}
            helperText={amountInvalid ? t('invoices.amountInvalid') : t('invoices.create.amountHint')}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ 'aria-label': t('invoices.amount') }}
          />
          {catalogQuery.isError ? (
            <Alert severity="error">{CURRENCIES_LOAD_ERROR}</Alert>
          ) : currencies.length > 0 ? (
            <TextField
              select
              label={t('invoices.currency')}
              fullWidth
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              inputProps={{ 'aria-label': t('invoices.currency') }}
            >
              {currencies.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.code}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <DateTimeField
            label={t('invoices.invoiceDate')}
            value={invoiceDate || null}
            onChange={(iso) => setInvoiceDate(iso ?? '')}
            clearable
            aria-label={t('invoices.invoiceDate')}
          />
          <DateTimeField
            label={t('invoices.dueDate')}
            value={dueDate || null}
            onChange={(iso) => setDueDate(iso ?? '')}
            clearable
            aria-label={t('invoices.dueDate')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending || !canSubmit}
        >
          {t('invoices.create.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
